import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Shield, ShieldCheck, ShieldAlert, Users, Ban, Eye, BookOpen, HelpCircle, FolderOpen, Megaphone, Mail, MessageSquareText, MessageCircle, Check, X, Upload, Trash2, LifeBuoy } from 'lucide-react';
import { toast } from 'sonner';
import EmergencyButton from '../components/EmergencyButton';
import ContentManager from '../components/ContentManager';
import DocumentLibrary from '../components/DocumentLibrary';
import BannerManager from '../components/BannerManager';
import TranslationStatusWidget from '../components/TranslationStatusWidget';
import MarketingEngagementWidget from '../components/MarketingEngagementWidget';
import AiRecipeLeaderboard from '../components/AiRecipeLeaderboard';
import SuperAdminCheckinReport from './SuperAdminCheckinReport';
import DevPreviewWeekWidget from '../components/DevPreviewWeekWidget';
import PartnerDietAnalyticsWidget from '../components/PartnerDietAnalyticsWidget';
import { authFetch } from '../lib/authFetch';
import { BackToTopButton } from '../components/NavigationHelpers';
import useAllowPrint from '../components/useAllowPrint';

function InviteEmailTab() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [inviteCode, setInviteCode] = useState('AXIOM2026');
  const [sending, setSending] = useState(false);
  const [sentInvites, setSentInvites] = useState([]);

  useEffect(() => { fetchSentInvites(); }, []);

  const fetchSentInvites = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/sent-invites`);
      if (response.ok) setSentInvites(await response.json());
    } catch (e) { console.error(e); }
  };

  const sendInvite = async () => {
    if (!email.trim()) { toast.error('Email is required'); return; }
    setSending(true);
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/send-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), name: name.trim(), invite_code: inviteCode })
      });
      if (response.ok) {
        toast.success(`Invite sent to ${email}`);
        setEmail('');
        setName('');
        fetchSentInvites();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to send invite');
      }
    } catch (e) { toast.error('Failed to send invite'); }
    setSending(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-zinc-200 rounded-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <Mail className="w-6 h-6 text-blue-600" />
          <h2 className="text-lg font-bold text-zinc-950">Invite Beta Testers</h2>
        </div>
        <p className="text-sm text-zinc-600 mb-6">Send personalised invitations to beta testers. The email includes the Axiom branding, an invite code and a direct link to the programme.</p>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider block mb-1">Recipient Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="John Smith" className="h-10" data-testid="invite-name" />
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider block mb-1">Email Address *</label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="john@example.com" type="email" className="h-10" data-testid="invite-email" />
          </div>
          <div>
            <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider block mb-1">Invite Code</label>
            <select value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm bg-white" data-testid="invite-code-select">
              <option value="AXIOM2026">AXIOM2026</option>
              <option value="ARP2026">ARP2026</option>
              <option value="RECOVERY">RECOVERY</option>
              <option value="SILKWORTH">SILKWORTH</option>
              <option value="TUDOR8">TUDOR8</option>
            </select>
          </div>
          <Button onClick={sendInvite} disabled={sending} className="w-full bg-blue-600 text-white hover:bg-blue-700 h-11" data-testid="send-invite-btn">
            <Mail className="w-4 h-4 mr-2" />
            {sending ? 'Sending...' : 'Send Invite Email'}
          </Button>
        </div>
      </div>

      {/* Sent Invites History */}
      <div className="bg-white border border-zinc-200 rounded-md p-6">
        <h3 className="text-sm font-bold text-zinc-950 uppercase tracking-wider mb-3">Sent Invites ({sentInvites.length})</h3>
        {sentInvites.length === 0 ? (
          <p className="text-sm text-zinc-500">No invites sent yet.</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {sentInvites.map((inv, i) => (
              <div key={`${inv.email}-${inv.sent_at || i}`} className="flex items-center justify-between bg-zinc-50 border border-zinc-100 rounded-md p-3">
                <div>
                  <p className="text-sm font-semibold text-zinc-900">{inv.email}</p>
                  <p className="text-xs text-zinc-500">{inv.name || 'No name'} &middot; Code: {inv.invite_code}</p>
                </div>
                <span className="text-[10px] text-zinc-400">{new Date(inv.sent_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ResearchSuggestionsTab() {
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => { fetchSuggestions(); }, []);

  const fetchSuggestions = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/research-suggestions`);
      if (response.ok) setSuggestions(await response.json());
    } catch (e) { console.error(e); }
  };

  const updateStatus = async (id, status) => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/research-suggestions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        toast.success(`Suggestion ${status}`);
        fetchSuggestions();
      }
    } catch (e) { toast.error('Failed to update'); }
  };

  const pending = suggestions.filter(s => s.status === 'pending');
  const reviewed = suggestions.filter(s => s.status !== 'pending');

  return (
    <div className="space-y-6">
      <div className="bg-white border border-zinc-200 rounded-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <MessageSquareText className="w-6 h-6 text-blue-600" />
          <h2 className="text-lg font-bold text-zinc-950">Research Suggestions</h2>
        </div>
        <p className="text-sm text-zinc-600 mb-4">Review research material suggestions submitted by programme users. Approved suggestions can be added to the Research Library.</p>

        {/* Pending */}
        <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-3">Pending Review ({pending.length})</h3>
        {pending.length === 0 ? (
          <p className="text-sm text-zinc-500 mb-6">No pending suggestions.</p>
        ) : (
          <div className="space-y-3 mb-6">
            {pending.map(s => (
              <div key={s.suggestion_id} className="bg-amber-50 border border-amber-200 rounded-md p-4" data-testid={`suggestion-${s.suggestion_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">{s.category}</span>
                    <h4 className="text-sm font-semibold text-zinc-900 mt-0.5">{s.title}</h4>
                    {s.description && <p className="text-xs text-zinc-600 mt-1">{s.description}</p>}
                    {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline mt-1 block truncate">{s.url}</a>}
                    <p className="text-[10px] text-zinc-400 mt-2">By {s.user_name} &middot; {new Date(s.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <Button onClick={() => updateStatus(s.suggestion_id, 'approved')} size="sm" className="bg-green-600 hover:bg-green-700 text-white h-8 px-3">
                      <Check className="w-3.5 h-3.5" />
                    </Button>
                    <Button onClick={() => updateStatus(s.suggestion_id, 'rejected')} size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-8 px-3">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Reviewed */}
        {reviewed.length > 0 && (
          <>
            <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-3">Reviewed ({reviewed.length})</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {reviewed.map(s => (
                <div key={s.suggestion_id} className={`border rounded-md p-3 ${s.status === 'approved' ? 'bg-green-50 border-green-200' : 'bg-zinc-50 border-zinc-200'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider mr-2 text-zinc-500">{s.category}</span>
                      <span className="text-sm font-medium text-zinc-800">{s.title}</span>
                    </div>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${s.status === 'approved' ? 'bg-green-600 text-white' : 'bg-zinc-400 text-white'}`}>
                      {s.status}
                    </span>
                  </div>
                  <p className="text-[10px] text-zinc-400 mt-1">By {s.user_name} &middot; Reviewed by {s.reviewed_by}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// SuperAdmin tab: review beta tester feedback. Lists newest first,
// grouped by "new" (needs triage) then everything else. Each item
// shows category, submitter, page URL (clickable), timestamp and the
// full description. Admins can move items through the status flow
// (new → reviewed → actioned / wont_fix) and attach a private note.
function NativeAppPreferenceWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/beta-feedback/native-app-preference`
        );
        if (res.ok) setData(await res.json());
      } catch (e) {
        console.error('Failed to load native-app-preference aggregate:', e);
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return null;
  const counts = data?.counts || { yes: 0, no: 0, maybe: 0 };
  const total = data?.total || 0;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  const swatch = (label, n, color, testid) => (
    <div className="flex-1 min-w-[110px] rounded-md border border-zinc-200 bg-white p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className={`text-2xl font-bold ${color}`} data-testid={testid}>{n}</span>
        <span className="text-xs text-zinc-500">({pct(n)}%)</span>
      </div>
    </div>
  );

  return (
    <div
      className="mb-6 rounded-lg border border-blue-200 bg-blue-50/40 p-4"
      data-testid="native-app-preference-widget"
    >
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-bold text-zinc-900">
          Native App Preference: “Would users want an App Store / Google Play version?”
        </h3>
        <span className="text-xs text-zinc-500">{total} responses</span>
      </div>
      {total === 0 ? (
        <p className="text-xs text-zinc-500">No responses yet. Users see this question in the Feedback widget.</p>
      ) : (
        <div className="flex gap-3 flex-wrap">
          {swatch('Yes', counts.yes, 'text-green-700', 'native-app-count-yes')}
          {swatch('No', counts.no, 'text-red-700', 'native-app-count-no')}
          {swatch('Maybe', counts.maybe, 'text-amber-700', 'native-app-count-maybe')}
        </div>
      )}
    </div>
  );
}

function BetaFeedbackTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/beta-feedback`);
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } catch (e) {
      console.error('Failed to load beta feedback items:', e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchItems(); }, []);

  const updateStatus = async (id, status, note = null) => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/beta-feedback/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, admin_note: note }),
      });
      if (res.ok) {
        toast.success(`Marked as ${status}`);
        fetchItems();
      } else {
        toast.error('Update failed');
      }
    } catch { toast.error('Update failed'); }
  };

  const tint = (cat) => ({
    bug: 'bg-red-100 text-red-800 border-red-200',
    suggestion: 'bg-amber-100 text-amber-800 border-amber-200',
    ux: 'bg-violet-100 text-violet-800 border-violet-200',
    other: 'bg-zinc-100 text-zinc-800 border-zinc-200',
  }[cat] || 'bg-zinc-100 text-zinc-800');

  const statusTint = (s) => ({
    new: 'bg-blue-500 text-white',
    reviewed: 'bg-zinc-500 text-white',
    actioned: 'bg-green-600 text-white',
    wont_fix: 'bg-zinc-300 text-zinc-700',
  }[s] || 'bg-zinc-200 text-zinc-700');

  const newItems = items.filter(i => i.status === 'new');
  const otherItems = items.filter(i => i.status !== 'new');

  const renderItem = (fb) => {
    const isOpen = expanded[fb.feedback_id];
    return (
      <div key={fb.feedback_id} className="bg-white border border-zinc-200 rounded-md p-4" data-testid={`feedback-${fb.feedback_id}`}>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${tint(fb.category)}`}>
              {fb.category.toUpperCase()}
            </span>
            <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${statusTint(fb.status)}`}>
              {fb.status.replace('_', ' ')}
            </span>
            <span className="text-xs text-zinc-500">{new Date(fb.created_at).toLocaleString()}</span>
          </div>
          <button
            onClick={() => setExpanded(e => ({ ...e, [fb.feedback_id]: !isOpen }))}
            className="text-xs text-zinc-500 hover:text-zinc-900"
          >
            {isOpen ? 'Collapse' : 'Expand'}
          </button>
        </div>
        <p className="text-sm text-zinc-800 whitespace-pre-wrap">
          {isOpen ? fb.description : (fb.description.length > 140 ? fb.description.slice(0, 140) + '…' : fb.description)}
        </p>
        {isOpen && (
          <div className="mt-3 space-y-1 text-xs text-zinc-600">
            <div><strong>From:</strong> {fb.user_name || '—'} &lt;{fb.user_email}&gt; ({fb.user_role})</div>
            {fb.page_url && (
              <div>
                <strong>Page:</strong>{' '}
                <a href={fb.page_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">
                  {fb.page_url}
                </a>
              </div>
            )}
            {fb.user_agent && <div><strong>UA:</strong> <span className="break-all">{fb.user_agent}</span></div>}
            {fb.admin_note && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-900">
                <strong>Note:</strong> {fb.admin_note}
              </div>
            )}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {fb.status !== 'reviewed' && (
            <Button onClick={() => updateStatus(fb.feedback_id, 'reviewed')} size="sm" variant="outline" className="h-8 px-3 text-xs">
              Mark Reviewed
            </Button>
          )}
          {fb.status !== 'actioned' && (
            <Button onClick={() => updateStatus(fb.feedback_id, 'actioned')} size="sm" className="bg-green-600 hover:bg-green-700 text-white h-8 px-3 text-xs">
              Mark Actioned
            </Button>
          )}
          {fb.status !== 'wont_fix' && (
            <Button onClick={() => updateStatus(fb.feedback_id, 'wont_fix')} size="sm" variant="outline" className="h-8 px-3 text-xs text-zinc-600">
              Won&apos;t fix
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="beta-feedback-tab">
      <div className="flex items-center gap-3 mb-4">
        <MessageCircle className="w-6 h-6 text-zinc-900" />
        <h2 className="text-lg font-bold text-zinc-950">Beta Feedback</h2>
        <span className="ml-auto text-xs text-zinc-500">{items.length} total</span>
      </div>
      <p className="text-sm text-zinc-600 mb-4">Review feedback submitted by beta testers via the floating Feedback button. Newest first.</p>

      <NativeAppPreferenceWidget />

      {loading && <p className="text-sm text-zinc-500">Loading…</p>}
      {!loading && items.length === 0 && <p className="text-sm text-zinc-500">No feedback yet. Beta testers can submit via the floating &quot;Feedback&quot; button.</p>}

      {newItems.length > 0 && (
        <>
          <h3 className="text-xs font-bold uppercase tracking-wide text-blue-600 mt-4 mb-2">New: needs triage ({newItems.length})</h3>
          <div className="space-y-3 mb-6">{newItems.map(renderItem)}</div>
        </>
      )}
      {otherItems.length > 0 && (
        <>
          <h3 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mt-4 mb-2">Reviewed ({otherItems.length})</h3>
          <div className="space-y-3">{otherItems.map(renderItem)}</div>
        </>
      )}
    </div>
  );
}

function RecoveryRoomPurgeTab() {
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [flagged, setFlagged] = useState([]);
  const [loadingFlagged, setLoadingFlagged] = useState(false);
  const [actingId, setActingId] = useState(null);

  const fetchFlagged = async () => {
    setLoadingFlagged(true);
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/moderation/flagged`);
      const d = await r.json();
      if (r.ok) setFlagged(d.items || []);
    } catch (e) {
      // silent
    } finally {
      setLoadingFlagged(false);
    }
  };

  useEffect(() => {
    fetchFlagged();
  }, []);

  const reinstate = async (id) => {
    setActingId(id);
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/moderation/${id}/reinstate`, { method: 'POST' });
      if (r.ok) {
        toast.success('Message reinstated.');
        setFlagged(prev => prev.filter(m => m.message_id !== id));
      } else {
        toast.error('Reinstate failed.');
      }
    } finally {
      setActingId(null);
    }
  };

  const removeMsg = async (id) => {
    if (!window.confirm('Permanently remove this message? This cannot be undone.')) return;
    setActingId(id);
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/moderation/${id}/remove`, { method: 'POST' });
      if (r.ok) {
        toast.success('Message removed.');
        setFlagged(prev => prev.filter(m => m.message_id !== id));
      } else {
        toast.error('Remove failed.');
      }
    } finally {
      setActingId(null);
    }
  };

  const handlePurge = async () => {
    if (confirmText !== 'PURGE') {
      toast.error('Type PURGE to confirm.');
      return;
    }
    setBusy(true);
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/recovery-room/purge-all`, {
        method: 'POST',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Purge failed');
      setLastResult(d);
      setConfirmText('');
      toast.success(`Recovery Room cleared. ${d.messages_deleted} messages, ${d.cooked_events_deleted} cook events, ${d.cook_invites_deleted} invites removed.`);
      fetchFlagged();
    } catch (e) {
      toast.error(e.message || 'Purge failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="recovery-room-tab">
      {/* Moderation Queue */}
      <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="moderation-queue">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-6 h-6 text-amber-600" />
            <h2 className="text-lg font-bold text-zinc-950">Moderation Queue</h2>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900">
              {flagged.length}
            </span>
          </div>
          <button
            onClick={fetchFlagged}
            className="text-xs text-zinc-500 hover:text-zinc-900 underline"
            data-testid="mod-queue-refresh"
          >
            Refresh
          </button>
        </div>
        <p className="text-xs text-zinc-600 leading-relaxed mb-4">
          Every message flagged by the AI Sentinel or a member is hidden from
          the Recovery Room and appears here for review.  Reinstate a false
          flag or permanently remove content that breaks the room's rules.
        </p>
        {loadingFlagged ? (
          <div className="text-sm text-zinc-500 py-6 text-center">Loading…</div>
        ) : flagged.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center bg-zinc-50 rounded-md border border-dashed border-zinc-200">
            No flagged messages. The room is clean.
          </div>
        ) : (
          <div className="space-y-3">
            {flagged.map((m) => (
              <div key={m.message_id} className="bg-red-50 border border-red-200 rounded-md p-4" data-testid={`flagged-item-${m.message_id}`}>
                <div className="flex items-center justify-between mb-2 text-xs">
                  <div className="flex items-center gap-2 text-zinc-700">
                    <span className="font-bold">{m.poster_current_handle}</span>
                    <span className="text-zinc-400">·</span>
                    <span className="text-zinc-500">{m.room || 'Global'}</span>
                    <span className="text-zinc-400">·</span>
                    <span className="text-zinc-500">{new Date(m.created_at).toLocaleString()}</span>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-semibold">
                    {m.flag_reason || 'Flagged'}
                  </span>
                </div>
                <p className="text-sm text-zinc-800 whitespace-pre-wrap mb-3">{m.content}</p>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => reinstate(m.message_id)}
                    disabled={actingId === m.message_id}
                    className="bg-zinc-900 text-white hover:bg-zinc-800 h-8 px-3 text-xs"
                    data-testid={`reinstate-${m.message_id}`}
                  >
                    <Check className="w-3 h-3 mr-1" /> Reinstate
                  </Button>
                  <Button
                    onClick={() => removeMsg(m.message_id)}
                    disabled={actingId === m.message_id}
                    className="bg-red-600 text-white hover:bg-red-700 h-8 px-3 text-xs"
                    data-testid={`remove-${m.message_id}`}
                  >
                    <X className="w-3 h-3 mr-1" /> Remove permanently
                  </Button>
                  {m.user_flag_count > 0 && (
                    <span className="text-xs text-zinc-500 ml-auto">
                      {m.user_flag_count} community flag{m.user_flag_count === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Purge Card */}
      <div className="bg-white border border-zinc-200 rounded-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <Trash2 className="w-6 h-6 text-red-600" />
          <h2 className="text-lg font-bold text-zinc-950">Recovery Room · Content Purge</h2>
        </div>
        <p className="text-sm text-zinc-700 leading-relaxed mb-4">
          Removes every message from every Recovery Room (Australia, Global,
          Women, Men, LGBTQI+ and any others), plus the "Recently Cooked"
          shared-recipe strip and the "cook-together invites" counter used on
          the landing page. The rooms themselves and every user account /
          pseudonym / DM / self-work item are preserved.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-5 text-xs text-amber-900 leading-relaxed">
          <strong>Use before go-live only.</strong> This is a one-shot action to
          clear pre-launch test posts so real members enter an empty room. It
          is destructive and cannot be undone.
        </div>
        <label className="block text-xs font-semibold text-zinc-700 mb-2 uppercase tracking-wider">
          Type <code className="font-mono bg-zinc-100 px-1 rounded">PURGE</code> to enable the button
        </label>
        <Input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="PURGE"
          className="h-11 max-w-xs mb-3 font-mono"
          data-testid="purge-confirm-input"
        />
        <Button
          onClick={handlePurge}
          disabled={busy || confirmText !== 'PURGE'}
          className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
          data-testid="purge-recovery-room-btn"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          {busy ? 'Purging…' : 'Purge Recovery Room content'}
        </Button>
        {lastResult && (
          <div className="mt-5 bg-emerald-50 border border-emerald-200 rounded-md p-4 text-xs text-emerald-900">
            <div className="font-bold mb-1">Purge complete</div>
            <div>Messages deleted: <span className="font-mono">{lastResult.messages_deleted}</span></div>
            <div>Cook events deleted: <span className="font-mono">{lastResult.cooked_events_deleted}</span></div>
            <div>Cook invites deleted: <span className="font-mono">{lastResult.cook_invites_deleted}</span></div>
            <div className="mt-1 opacity-70">By {lastResult.purged_by} at {new Date(lastResult.purged_at).toLocaleString()}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SAFETY & NOTIFICATION FLAGS (Feb 2026 privacy hold)
// ═══════════════════════════════════════════════════════════════════════════
// Three INDEPENDENT flags — CSSRS_SCREENING_ENABLED, SAFETY_QUESTIONS_ENABLED,
// EMERGENCY_CONTACT_NOTIFICATION_ENABLED.  Each has its own hashed audit trail
// and toggle endpoint.  Re-enabling any one flag later cannot silently
// re-enable another.

function FlagPanel({
  title,
  subtitle,
  onDescription,
  offDescription,
  endpointSlug,
  envVarLabel,
  emphasise,
  testIdBase,
}) {
  const [state, setState] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/mental-health/${endpointSlug}`),
        authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/mental-health/${endpointSlug}/audit`),
      ]);
      if (s.ok) setState(await s.json());
      if (a.ok) setAudit((await a.json()).entries || []);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointSlug]);

  const enabled = !!state?.enabled;
  const nextState = !enabled;
  const confirmWord = nextState ? 'ENABLE' : 'DISABLE';

  const toggle = async () => {
    if (confirmText !== confirmWord) {
      toast.error(`Type ${confirmWord} to confirm.`);
      return;
    }
    if (!reason.trim()) {
      toast.error('Reason (e.g. sign-off reference) is required for the audit trail.');
      return;
    }
    setBusy(true);
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/mental-health/${endpointSlug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: nextState, reason: reason.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Toggle failed');
      toast.success(`${title} is now ${nextState ? 'ENABLED' : 'DISABLED'}. Audit hash ${d.audit_entry_hash.slice(0, 12)}.`);
      setConfirmText('');
      setReason('');
      fetchAll();
    } catch (e) {
      toast.error(e.message || 'Toggle failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid={`${testIdBase}-panel`}>
      <div className="flex items-center gap-3 mb-3">
        <LifeBuoy className={`w-5 h-5 ${enabled ? (emphasise ? 'text-red-600' : 'text-amber-600') : 'text-zinc-400'}`} />
        <h3 className="text-base font-bold text-zinc-950">{title}</h3>
        {loading ? (
          <span className="ml-auto inline-block w-16 h-5 bg-zinc-100 animate-pulse rounded" />
        ) : (
          <span
            className={`ml-auto text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider ${
              enabled ? (emphasise ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-900') : 'bg-zinc-100 text-zinc-700'
            }`}
            data-testid={`${testIdBase}-state`}
          >
            {enabled ? 'ENABLED' : 'DISABLED'}
          </span>
        )}
      </div>
      {subtitle && <p className="text-xs text-zinc-500 leading-relaxed mb-2">{subtitle}</p>}
      <p className="text-xs text-zinc-700 leading-relaxed mb-3">
        <strong>When ON:</strong> {onDescription} <br />
        <strong>When OFF:</strong> {offDescription}
      </p>
      <p className="text-[10px] text-zinc-400 font-mono mb-3">env var: {envVarLabel}</p>

      <div className="border-t border-zinc-100 pt-3">
        <label className="block text-xs font-semibold text-zinc-700 mb-1 uppercase tracking-wider">
          Reason / sign-off reference (required: recorded in audit trail)
        </label>
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Reviewer sign-off ref #ABC-2026-07"
          className="h-10 mb-2 text-sm"
          data-testid={`${testIdBase}-reason`}
        />
        <label className="block text-xs font-semibold text-zinc-700 mb-1 uppercase tracking-wider">
          Type <code className="font-mono bg-zinc-100 px-1 rounded">{confirmWord}</code> to enable the button
        </label>
        <div className="flex gap-2">
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={confirmWord}
            className="h-10 font-mono text-sm flex-1"
            data-testid={`${testIdBase}-confirm`}
          />
          <Button
            onClick={toggle}
            disabled={busy || confirmText !== confirmWord || !reason.trim()}
            className={`${
              nextState && emphasise ? 'bg-red-600 hover:bg-red-700' : 'bg-zinc-900 hover:bg-zinc-800'
            } text-white disabled:opacity-40`}
            data-testid={`${testIdBase}-toggle`}
          >
            {busy ? 'Applying…' : nextState ? 'Enable' : 'Disable'}
          </Button>
        </div>
      </div>

      {/* Audit trail (chained SHA-256) */}
      <div className="mt-4 border-t border-zinc-100 pt-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Audit Trail · SHA-256 chain</h4>
          <button onClick={fetchAll} className="text-[10px] text-zinc-500 hover:text-zinc-900 underline" data-testid={`${testIdBase}-audit-refresh`}>
            Refresh
          </button>
        </div>
        {audit.length === 0 ? (
          <p className="text-xs text-zinc-500 py-3 text-center bg-zinc-50 rounded-md border border-dashed border-zinc-200">
            No flag changes recorded yet.
          </p>
        ) : (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {audit.map((e) => (
              <div key={e.entry_hash} className="border border-zinc-200 rounded p-2 text-[11px] bg-zinc-50" data-testid={`${testIdBase}-audit-${e.entry_hash.slice(0, 8)}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${e.new ? 'bg-red-500' : 'bg-zinc-400'}`} />
                  <span className="font-bold text-zinc-900">{String(e.previous)} → {String(e.new)}</span>
                  <span className="text-zinc-500">·</span>
                  <span className="text-zinc-500">{new Date(e.at).toLocaleString()}</span>
                  <span className="text-zinc-500">·</span>
                  <span className="text-zinc-600">{e.actor_email}</span>
                </div>
                {e.reason && (
                  <div className="text-zinc-700 mt-0.5 italic pl-3.5">"{e.reason}"</div>
                )}
                <div className="text-[9px] text-zinc-400 font-mono pl-3.5 mt-0.5 break-all">
                  prev: {e.previous_hash.slice(0, 12)}… → this: {e.entry_hash.slice(0, 12)}…
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CssrsFlagTab() {
  return (
    <div className="space-y-4" data-testid="cssrs-flag-tab">
      <div className="bg-amber-50 border-2 border-amber-300 rounded-md p-4">
        <h2 className="text-sm font-bold uppercase tracking-widest text-amber-900 mb-1">Safety & Notification Flags · Feb 2026 Privacy Hold</h2>
        <p className="text-xs text-amber-900 leading-relaxed">
          Three <strong>independent</strong> flags.  Re-enabling one cannot silently re-enable another.  All three default to OFF in production until the reviewer sign-off arrives: record the sign-off reference in the "Reason" field on every toggle.
        </p>
        <p className="text-xs text-amber-900 leading-relaxed mt-2">
          <strong>PHQ-9 and GAD-7 are not affected by any of these flags</strong> in any state.
        </p>
      </div>

      <FlagPanel
        title="Emergency-Contact Notification"
        subtitle="Hard kill switch on the auto-dispatch that emailed a user's emergency contacts on any red-tier result.  This is the notification code path itself: off means the function returns immediately without touching Resend or reading the contacts collection, regardless of which trigger reached it."
        onDescription="Any red-tier check-in result auto-emails the user's emergency contacts."
        offDescription="No third-party notification fires for any reason.  Red-tier UI shows crisis lines only."
        endpointSlug="emergency-notification-flag"
        envVarLabel="EMERGENCY_CONTACT_NOTIFICATION_ENABLED"
        emphasise
        testIdBase="notification-flag"
      />

      <FlagPanel
        title="Safety-Questions Section"
        subtitle="Whether the safety-questions block appears in the Wellbeing check-in.  When off, users see the static Interim Crisis Resources screen where the safety questions used to sit."
        onDescription="Safety-question block renders in the Wellbeing check-in and contributes to the tier."
        offDescription="Interim Crisis Resources screen shown instead.  No safety-question score contributes to the tier."
        endpointSlug="safety-questions-flag"
        envVarLabel="SAFETY_QUESTIONS_ENABLED"
        testIdBase="safety-flag"
      />

      <FlagPanel
        title="C-SSRS Clinical Screener"
        subtitle="The three Columbia-Suicide Severity Rating Scale questions.  Doubly gated: requires BOTH this flag AND the Safety-Questions flag to be on for C-SSRS to actually render."
        onDescription="The three C-SSRS items render in the safety-questions block (assuming the safety-questions flag is also on)."
        offDescription="No C-SSRS score is calculated, stored or referenced.  PHQ-9 item 9 can still trigger red on its own."
        endpointSlug="cssrs-flag"
        envVarLabel="CSSRS_SCREENING_ENABLED"
        testIdBase="cssrs-flag"
      />
    </div>
  );
}


function SuperAdminPage() {
  useAllowPrint();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('users');

  // 2FA state
  const [twoFAStatus, setTwoFAStatus] = useState(null);
  const [qrCode, setQRCode] = useState(null);
  const [totpSecret, setTotpSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [setting2FA, setSetting2FA] = useState(false);

  // Security state
  const [securityStats, setSecurityStats] = useState(null);

  useEffect(() => {
    if (user?.role !== 'superadmin') {
      navigate('/dashboard');
      toast.error('SuperAdmin access required');
      return;
    }
    fetchUsers();
    fetch2FAStatus();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`);
      if (response.ok) setUsers(await response.json());
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/user/${userId}`);
      if (response.ok) setSelectedUser(await response.json());
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const exportCriminalJusticeReport = async (userId) => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/criminal-justice/${userId}`);
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `criminal-justice-report-${userId}-${new Date().toISOString()}.json`;
        a.click();
        toast.success('Report downloaded');
      }
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  // 2FA Functions
  const fetch2FAStatus = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/status`);
      if (response.ok) setTwoFAStatus(await response.json());
    } catch (error) {
      console.error('Failed to fetch 2FA status');
    }
  };

  const start2FASetup = async () => {
    setSetting2FA(true);
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/setup`, {
        method: 'POST',
              });
      if (response.ok) {
        const data = await response.json();
        setQRCode(data.qr_code);
        setTotpSecret(data.secret);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to start 2FA setup');
      }
    } catch (error) {
      toast.error('Failed to start 2FA setup');
    }
  };

  const verify2FASetup = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: verifyCode })
      });
      if (response.ok) {
        toast.success('2FA enabled successfully!');
        setQRCode(null);
        setTotpSecret('');
        setVerifyCode('');
        setSetting2FA(false);
        fetch2FAStatus();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Invalid code');
      }
    } catch (error) {
      toast.error('Verification failed');
    }
  };

  const disable2FA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Please enter your current authenticator code');
      return;
    }
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: disableCode })
      });
      if (response.ok) {
        toast.success('2FA disabled');
        setDisableCode('');
        fetch2FAStatus();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to disable 2FA');
      }
    } catch (error) {
      toast.error('Failed to disable 2FA');
    }
  };

  // Security Functions
  const fetchSecurityStats = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/security/stats`);
      if (response.ok) setSecurityStats(await response.json());
    } catch (error) {
      toast.error('Failed to load security stats');
    }
  };

  const unbanIP = async (ip) => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/security/unban/${ip}`, {
        method: 'POST',
              });
      if (response.ok) {
        toast.success(`IP ${ip} unbanned`);
        fetchSecurityStats();
      }
    } catch (error) {
      toast.error('Failed to unban IP');
    }
  };

  const tabs = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'content', label: 'Content', icon: BookOpen },
    { id: 'arp-uploads', label: 'ARP Uploads', icon: Upload },
    { id: 'family-course', label: 'Courses', icon: Mail },
    { id: 'uds', label: 'UDS / Pathology', icon: FolderOpen },
    { id: 'reports', label: 'Reports', icon: FolderOpen },
    { id: 'invite', label: 'Invite', icon: Mail },
    { id: 'research', label: 'Research', icon: MessageSquareText },
    { id: 'beta-feedback', label: 'Beta Feedback', icon: MessageCircle },
    { id: 'recovery-room', label: 'Recovery Room (RR)', icon: Trash2 },
    { id: 'checkin-report', label: 'Check-in Results (anon)', icon: LifeBuoy },
    { id: '2fa', label: '2FA Setup', icon: ShieldCheck },
    { id: 'documents', label: 'Documents', icon: FolderOpen },
    { id: 'banners', label: 'Weekly Banners', icon: Megaphone },
    { id: 'security', label: 'Security', icon: ShieldAlert },
    { id: 'guide', label: 'Admin Guide', icon: HelpCircle },
  ];

  return (
    <div className="min-h-screen bg-zinc-100">
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-zinc-200 z-40 h-16 px-4 flex items-center justify-between">
        <button onClick={() => navigate(-1)} data-testid="back-to-dashboard">
          <ArrowLeft className="w-5 h-5 text-zinc-950" />
        </button>
        <h1 className="text-lg font-semibold text-zinc-950">SuperAdmin</h1>
        <EmergencyButton />
      </header>

      <main className="pt-20 pb-6 px-4">
        <div className="max-w-4xl mx-auto">
          <DevPreviewWeekWidget />
          <PartnerDietAnalyticsWidget />
          <TranslationStatusWidget />
          <MarketingEngagementWidget />
          <AiRecipeLeaderboard />
          {/* Tab Navigation */}
          <div className="flex space-x-2 mb-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id === 'arp-uploads') { navigate('/superadmin/arp-uploads'); return; }
                  if (tab.id === 'family-course') { navigate('/superadmin/family-course'); return; }
                  if (tab.id === 'uds') { navigate('/superadmin/uds'); return; }
                  if (tab.id === 'reports') { navigate('/superadmin/reports'); return; }
                  setActiveTab(tab.id);
                  if (tab.id === 'security') fetchSecurityStats();
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-zinc-900 text-white'
                    : 'bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* ============ USERS TAB ============ */}
          {activeTab === 'users' && (
            <>
              {selectedUser ? (
                <div>
                  <Button onClick={() => setSelectedUser(null)} variant="outline" className="mb-4">
                    Back to List
                  </Button>
                  <div className="bg-white border border-zinc-200 rounded-md p-6 mb-4">
                    <h2 className="text-xl font-bold text-zinc-950 mb-4">{selectedUser.user.name}</h2>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-zinc-600">Email:</span>
                        <span className="font-semibold">{selectedUser.user.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-600">Current Week:</span>
                        <span className="font-semibold">Week {selectedUser.user.current_week}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-600">Payment Status:</span>
                        <span className="font-semibold">{selectedUser.user.payment_status}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-600">Total Journals:</span>
                        <span className="font-semibold">{selectedUser.stats.total_journals}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-600">Total Check-ins:</span>
                        <span className="font-semibold">{selectedUser.stats.total_checks}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-600">Total Meetings:</span>
                        <span className="font-semibold">{selectedUser.stats.total_meetings}</span>
                      </div>
                    </div>
                    <Button
                      onClick={() => exportCriminalJusticeReport(selectedUser.user.user_id)}
                      className="w-full mt-4 bg-red-600 text-white hover:bg-red-700"
                    >
                      Export Criminal Justice Report (Unencrypted)
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">All Users ({users.length})</p>
                  {users.map((u) => (
                    <div key={u.user_id} className="bg-white border border-zinc-200 rounded-md p-4 hover:border-zinc-300 transition-colors">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-semibold text-zinc-900">{u.name}</p>
                          <p className="text-xs text-zinc-500">{u.email}</p>
                          <p className="text-xs text-zinc-600 mt-1">Week {u.current_week} | {u.payment_status}</p>
                        </div>
                        <Button onClick={() => viewUserDetails(u.user_id)} variant="outline" size="sm">
                          View Details
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ============ CONTENT TAB ============ */}
          {activeTab === 'content' && (
            <ContentManager />
          )}

          {/* ============ INVITE TAB ============ */}
          {activeTab === 'invite' && (
            <InviteEmailTab />
          )}

          {/* ============ RESEARCH SUGGESTIONS TAB ============ */}
          {activeTab === 'research' && (
            <ResearchSuggestionsTab />
          )}

          {/* ============ BETA FEEDBACK TAB ============ */}
          {activeTab === 'beta-feedback' && (
            <BetaFeedbackTab />
          )}

          {/* ============ RECOVERY ROOM TAB ============ */}
          {activeTab === 'recovery-room' && (
            <RecoveryRoomPurgeTab />
          )}

          {/* ============ CHECK-IN REPORT TAB (Sep 2026, replaces retired C-SSRS flag tab) ============ */}
          {activeTab === 'checkin-report' && (
            <SuperAdminCheckinReport />
          )}

          {/* ============ 2FA TAB ============ */}
          {activeTab === '2fa' && (
            <div className="space-y-6">
              <div className="bg-white border border-zinc-200 rounded-md p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="w-6 h-6 text-zinc-900" />
                  <h2 className="text-lg font-bold text-zinc-950">Two-Factor Authentication</h2>
                </div>

                {/* Current Status */}
                <div className={`p-4 rounded-md mb-6 ${
                  twoFAStatus?.totp_enabled 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-amber-50 border border-amber-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {twoFAStatus?.totp_enabled ? (
                      <>
                        <ShieldCheck className="w-5 h-5 text-green-600" />
                        <span className="text-sm font-semibold text-green-800">2FA is ENABLED</span>
                      </>
                    ) : (
                      <>
                        <ShieldAlert className="w-5 h-5 text-amber-600" />
                        <span className="text-sm font-semibold text-amber-800">2FA is NOT enabled</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs mt-1 text-zinc-600">
                    {twoFAStatus?.totp_enabled 
                      ? 'Your account is protected with Google Authenticator.' 
                      : 'Enable 2FA to protect your SuperAdmin account with Google Authenticator.'}
                  </p>
                </div>

                {/* Setup Flow */}
                {!twoFAStatus?.totp_enabled && !qrCode && (
                  <Button
                    onClick={start2FASetup}
                    className="w-full bg-zinc-900 text-white hover:bg-zinc-800"
                    data-testid="enable-2fa-btn"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Enable Google Authenticator
                  </Button>
                )}

                {/* QR Code Display */}
                {qrCode && (
                  <div className="space-y-4" data-testid="2fa-setup-form">
                    <div className="text-center">
                      <p className="text-sm text-zinc-700 mb-4">
                        Scan this QR code with Google Authenticator on your phone:
                      </p>
                      <img 
                        src={qrCode} 
                        alt="2FA QR Code" 
                        className="mx-auto border border-zinc-200 rounded-md"
                        data-testid="2fa-qr-code"
                      />
                    </div>

                    <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
                      <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1">Manual Entry Key</p>
                      <code className="text-sm font-mono text-zinc-900 select-all break-all">{totpSecret}</code>
                    </div>

                    <div>
                      <p className="text-sm text-zinc-700 mb-2">Enter the 6-digit code from your authenticator app:</p>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          value={verifyCode}
                          onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                          placeholder="000000"
                          className="text-center text-xl tracking-[0.5em] font-mono"
                          data-testid="2fa-verify-input"
                        />
                        <Button 
                          onClick={verify2FASetup}
                          className="bg-green-600 text-white hover:bg-green-700"
                          data-testid="2fa-verify-btn"
                        >
                          Verify
                        </Button>
                      </div>
                    </div>

                    <Button
                      onClick={() => { setQRCode(null); setTotpSecret(''); setSetting2FA(false); }}
                      variant="outline"
                      className="w-full"
                    >
                      Cancel Setup
                    </Button>
                  </div>
                )}

                {/* Disable 2FA */}
                {twoFAStatus?.totp_enabled && (
                  <div className="border-t border-zinc-200 pt-4 mt-4">
                    <p className="text-sm text-zinc-600 mb-2">To disable 2FA, enter your current authenticator code:</p>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={disableCode}
                        onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                        placeholder="000000"
                        className="text-center text-xl tracking-[0.5em] font-mono"
                        data-testid="2fa-disable-input"
                      />
                      <Button 
                        onClick={disable2FA}
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid="2fa-disable-btn"
                      >
                        Disable 2FA
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ============ DOCUMENTS TAB ============ */}
          {activeTab === 'documents' && (
            <DocumentLibrary />
          )}

          {/* ============ BANNERS TAB ============ */}
          {activeTab === 'banners' && (
            <BannerManager />
          )}

          {/* ============ SECURITY TAB ============ */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white border border-zinc-200 rounded-md p-4">
                  <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Active Bans</p>
                  <p className="text-2xl font-black text-zinc-900 mt-1" data-testid="active-bans-count">
                    {securityStats?.active_bans || 0}
                  </p>
                </div>
                <div className="bg-white border border-zinc-200 rounded-md p-4">
                  <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Tracked IPs</p>
                  <p className="text-2xl font-black text-zinc-900 mt-1" data-testid="tracked-ips-count">
                    {securityStats?.tracked_ips || 0}
                  </p>
                </div>
              </div>

              {/* Banned IPs */}
              <div className="bg-white border border-zinc-200 rounded-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Ban className="w-5 h-5 text-red-600" />
                    <h3 className="text-sm font-bold text-zinc-950 uppercase tracking-wider">Banned IPs</h3>
                  </div>
                  <Button onClick={fetchSecurityStats} variant="outline" size="sm">Refresh</Button>
                </div>
                {securityStats?.banned_ips?.length > 0 ? (
                  <div className="space-y-2">
                    {securityStats.banned_ips.map((ban, i) => (
                      <div key={`${ban.ip}-${ban.banned_at || i}`} className="flex items-center justify-between bg-red-50 border border-red-100 rounded-md p-3">
                        <div>
                          <p className="text-sm font-mono font-semibold text-zinc-900">{ban.ip}</p>
                          <p className="text-xs text-zinc-600">{ban.reason}</p>
                          <p className="text-xs text-zinc-400">{ban.banned_at}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {ban.permanent && (
                            <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded-full font-bold">PERMANENT</span>
                          )}
                          <Button 
                            onClick={() => unbanIP(ban.ip)} 
                            variant="outline" 
                            size="sm"
                            className="text-xs"
                            data-testid={`unban-${ban.ip}`}
                          >
                            Unban
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-zinc-500">No banned IPs</p>
                )}
              </div>

              {/* Recent Intrusion Events */}
              <div className="bg-white border border-zinc-200 rounded-md p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Eye className="w-5 h-5 text-amber-600" />
                  <h3 className="text-sm font-bold text-zinc-950 uppercase tracking-wider">Recent Security Events</h3>
                </div>
                {securityStats?.recent_intrusions?.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {[...securityStats.recent_intrusions].reverse().map((event, i) => (
                      <div key={`${event.ip}-${event.timestamp || i}`} className={`text-xs p-2 rounded border ${
                        event.type === 'ip_banned' ? 'bg-red-50 border-red-100' :
                        event.type === 'honeypot' ? 'bg-red-100 border-red-200' :
                        event.type === 'rate_limit' ? 'bg-amber-50 border-amber-100' :
                        'bg-zinc-50 border-zinc-100'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className="font-mono font-semibold">{event.ip}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                            event.type === 'ip_banned' ? 'bg-red-600 text-white' :
                            event.type === 'honeypot' ? 'bg-red-800 text-white' :
                            event.type === 'rate_limit' ? 'bg-amber-500 text-white' :
                            'bg-zinc-500 text-white'
                          }`}>
                            {event.type.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        <p className="text-zinc-600 mt-1">{event.reason || event.email || event.path || ''}</p>
                        <p className="text-zinc-400">{event.timestamp}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-zinc-500">No security events recorded</p>
                )}
              </div>

              {/* Security Features Info */}
              <div className="bg-zinc-900 text-white rounded-md p-6">
                <h3 className="text-sm font-bold uppercase tracking-wider mb-3">Active Security Measures</h3>
                <div className="space-y-2 text-xs text-zinc-300">
                  <div className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-green-400" /> Rate limiting (200 req/min general, 30/min auth)</div>
                  <div className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-green-400" /> Brute force protection (5 attempts = lockout)</div>
                  <div className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-green-400" /> Progressive IP banning (warning &#8594; temp ban &#8594; permanent ban)</div>
                  <div className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-green-400" /> Honeypot traps (fake admin endpoints)</div>
                  <div className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-green-400" /> Attacker data collection (IP, device, location)</div>
                  <div className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-green-400" /> Escalating warning messages</div>
                  <div className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-green-400" /> TOTP 2FA for SuperAdmin accounts</div>
                </div>
              </div>
            </div>
          )}

          {/* ============ ADMIN GUIDE TAB ============ */}
          {activeTab === 'guide' && (
            <div className="space-y-4">
              <div className="bg-white border border-zinc-200 rounded-md p-6">
                <h2 className="text-lg font-bold text-zinc-950 mb-4">SuperAdmin Operations Guide</h2>
                <p className="text-sm text-zinc-600 mb-6">This guide is for SuperAdmin use only. It covers content management, user administration and platform operations.</p>
              </div>

              {/* File Naming Convention */}
              <div className="bg-white border border-zinc-200 rounded-md p-6">
                <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-3">File Naming Convention</h3>
                <div className="space-y-3 text-sm text-zinc-700">
                  <p className="text-xs text-zinc-600 leading-relaxed">
                    All new content uses the <strong>ARP_*</strong> protocol.  Drop the file into the
                    <strong> ARP Uploads</strong> tab and it is routed to the right collection automatically.
                  </p>
                  <div className="bg-zinc-50 rounded-md p-4 space-y-1">
                    <p className="font-mono text-xs">ARP_Disease_2WW_EN.pdf  ·  2 Week Window Disease module</p>
                    <p className="font-mono text-xs">ARP_Structure_2WW_EN.pdf  ·  2 Week Window Daily Structure</p>
                    <p className="font-mono text-xs">ARP_Lessons_Intro_EN.pdf  ·  Lessons introduction</p>
                    <p className="font-mono text-xs">ARP_Lesson_01_EN.pdf  ·  Lesson 1  (use 01..30)</p>
                    <p className="font-mono text-xs">ARP_Program_W1_EN.pdf  ·  Programme Week 1  (use W1..W30)</p>
                    <p className="font-mono text-xs">ARP_Program_W3_SW_EN.pdf  ·  Programme Self-Work for Week 3</p>
                    <p className="font-mono text-xs">ARP_Lesson_05_legend_EN.pdf  ·  optional second-file variant</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900 leading-relaxed">
                    <strong>Translations</strong>:  swap <code>EN</code> for <code>RU</code>, <code>UK</code>,
                    <code> KO</code>, <code>FR</code>, <code>IT</code>, <code>ES</code> or <code>PT</code>.
                    Same protocol, alpha-2 code in lowercase or uppercase.  Re-uploading any file overwrites that one language slot only;  structure and routing are preserved.
                  </div>
                </div>
              </div>

              {/* AI safeguards on uploads + translations */}
              <div className="bg-white border border-zinc-200 rounded-md p-6">
                <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-3">AI Safeguards on Content</h3>
                <div className="space-y-3 text-sm text-zinc-700 leading-relaxed">
                  <p>
                    <strong>English uploads</strong> run through the <em>AI-tell scrub</em> before being
                    persisted.  This deterministic pass removes em / en dashes, drops common
                    AI-tells ("It is important to note that", "Furthermore,", "In conclusion,", etc.),
                    and enforces two spaces after every full stop and one space after every comma.
                  </p>
                  <p>
                    <strong>Translations</strong> are produced by <strong>Claude Sonnet 4.5</strong>
                    under the strict unabridged protocol in <code className="font-mono bg-zinc-100 px-1 rounded">backend/translation_safeguards.py</code>.
                    Every chunk runs seven post-checks: paragraph parity, <code>{'<p>'}</code> /
                    <code>{'<li>'}</code> / <code>{'<h2-4>'}</code> tag-count parity, length
                    floor &amp; ceiling, forbidden summary markers, truncation tail,
                    abbreviation scan and a "target equals source" sanity guard.  Long documents
                    are chunked at paragraph boundaries (≤ 1500 words per call) so Claude never
                    sees a 3000+ word block that might tempt it to abbreviate.
                  </p>
                  <p>
                    Failed checks trigger a stricter retry: but only when the level-1 response
                    is clearly broken.  Otherwise the original chunk is kept (saves credits and
                    avoids a retry that usually produces the same mismatch).  Concurrency is
                    globally capped at <strong>2 LLM calls in flight</strong> across the entire
                    app to eliminate Anthropic rate-limit failures.
                  </p>
                  <p>
                    Saved translations are marked <strong>amber</strong> (machine-translated,
                    not yet human-verified).  Readers see them with the
                    "AI-translated · {'{language}'}" disclosure badge: full transparency.  Every
                    translation writes a row to
                    <code className="font-mono bg-zinc-100 px-1 rounded">translation_audits</code>
                    with chunk count, length ratio, warnings, attempt count and source / target
                    hashes for forensic review.
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
                    <strong>Honest disclosure, not censorship</strong>.  Machine-translated text
                    IS shown to readers with an amber badge so they know its provenance.  Future
                    human-verified translations will flip the badge to emerald.
                  </div>
                </div>
              </div>

              {/* Content Correction & Auto-Retranslate Workflow */}
              <div className="bg-white border border-indigo-200 rounded-md p-6" data-testid="guide-content-edit">
                <h3 className="text-sm font-bold text-indigo-900 uppercase tracking-wider mb-3">
                  Correcting or Replacing Content (Auto-Retranslate)
                </h3>
                <div className="space-y-3 text-sm text-zinc-700 leading-relaxed">
                  <p>
                    When you edit the <strong>English source</strong> of any reading or lesson via
                    the Content tab and click <strong>Save</strong>, the system automatically:
                  </p>
                  <ol className="list-decimal list-inside space-y-1 pl-2">
                    <li>Saves the new English text in <code className="font-mono bg-zinc-100 px-1 rounded">content_en</code>.</li>
                    <li><strong>Clears all 9 non-EN translations</strong> immediately: readers in
                      those languages see the English fallback with a &ldquo;translation pending&rdquo;
                      notice rather than stale localised copy.</li>
                    <li><strong>Queues 9 background translation tasks</strong>, one per supported
                      language, under the strict unabridged protocol.  Each task runs through the
                      global concurrency cap (max 2 Claude calls in flight) so the run never
                      trips rate limits.</li>
                    <li>Returns a <code className="font-mono bg-zinc-100 px-1 rounded">translate_task_ids</code> array.
                      The Translation Pipeline widget at the top of this page shows the
                      affected language counts drop by 1 and climb back as each translation
                      lands.</li>
                  </ol>
                  <div className="bg-zinc-50 rounded-md p-3 text-xs space-y-1">
                    <p className="font-semibold text-zinc-900">Metadata-only edits (title, estimated_time, pdf_url, etc.)</p>
                    <p>do NOT trigger retranslation: only changes to the English content body do.</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
                    <strong>Cost reminder</strong>: each English edit triggers 9 Claude translations
                    (~$0.30–$0.60 per language, ~$3–$5 per reading).  For a one-character typo this
                    is probably fine.  For a wholesale rewrite of a long Week reading, factor the
                    cost in.
                  </div>
                </div>
              </div>

              {/* Bulk operations (offline runner) */}
              <div className="bg-white border border-zinc-200 rounded-md p-6">
                <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-3">
                  Bulk Translation Operations (Offline Runner)
                </h3>
                <div className="space-y-3 text-sm text-zinc-700 leading-relaxed">
                  <p>
                    For multi-row operations (seeding all 30 weeks in all 9 languages, or
                    re-translating everything after a master-prompt refinement) use the offline
                    runner at <code className="font-mono bg-zinc-100 px-1 rounded">/app/scripts/bulk_translate_offline.py</code>.
                    It connects directly to Mongo and Claude: independent of the FastAPI
                    process: so a backend hot-reload cannot kill an in-flight run.
                  </p>
                  <div className="bg-zinc-50 rounded-md p-3 text-xs font-mono space-y-2">
                    <p className="text-zinc-500"># Translate every empty Programme cell:</p>
                    <p>cd /app/scripts && nohup python3 -u bulk_translate_offline.py \</p>
                    <p>{'    '}--section programme --concurrency 2 \</p>
                    <p>{'    > /app/logs/bulk_translate.log 2>&1 &'}</p>
                    <p className="text-zinc-500 pt-2"># Force re-translation of a specific row:</p>
                    <p>python3 bulk_translate_offline.py --section programme \</p>
                    <p>{'    '}--target-langs zh-TW --include-amber --limit 1</p>
                    <p className="text-zinc-500 pt-2"># Monitor progress:</p>
                    <p>python3 /app/scripts/monitor_translations.py</p>
                    <p>tail -f /app/logs/bulk_translate.log</p>
                  </div>
                  <p className="text-xs text-zinc-600">
                    The script is <strong>idempotent</strong>: it skips every cell that already
                    has <code>content_{'{lang}'}</code> populated.  Safe to interrupt and re-run.
                    Add <code className="font-mono bg-zinc-100 px-1 rounded">--include-amber</code>
                    to also overwrite previous AI translations.
                  </p>
                </div>
              </div>

              {/* Quality review workflow */}
              <div className="bg-white border border-zinc-200 rounded-md p-6">
                <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-3">
                  Quality Review &amp; Re-running a Single Cell
                </h3>
                <div className="space-y-3 text-sm text-zinc-700 leading-relaxed">
                  <p>
                    When a translation lands with
                    <code className="font-mono bg-zinc-100 px-1 rounded">translation_warnings_{'{lang}'}</code> non-empty,
                    Claude faithfully translated the content but the structure differs from the
                    source by one or more <code>{'<p>'}</code> / <code>{'<li>'}</code> tags.
                    Most warnings are harmless (Claude split a long sentence into two paragraphs
                    for grammar reasons).
                  </p>
                  <p>
                    The Translation Coverage Matrix at
                    <code className="font-mono bg-zinc-100 px-1 rounded">/superadmin/arp-uploads</code>
                    shows each cell colour-coded:
                  </p>
                  <ul className="space-y-1 pl-2 text-xs">
                    <li>🟢 <strong>Green</strong>: human-verified translation, no badge</li>
                    <li>🟡 <strong>Amber</strong>: AI-translated, amber badge on reader</li>
                    <li>⚪ <strong>Grey</strong>: empty, reader sees English fallback</li>
                    <li>🔴 <strong>Red</strong>: English source itself is missing or under 80 chars</li>
                  </ul>
                  <p>
                    To force a fresh attempt on one cell, hit the per-cell endpoint:
                  </p>
                  <div className="bg-zinc-50 rounded-md p-3 text-xs font-mono">
                    <p>POST /api/admin/arp-translate</p>
                    <p>{`{"section":"programme","key":"week-13","target_lang":"zh"}`}</p>
                  </div>
                  <p className="text-xs text-zinc-600">
                    Returns a <code>task_id</code>.  Poll
                    <code className="font-mono bg-zinc-100 px-1 rounded">GET /api/admin/arp-translate/status/{'{task_id}'}</code>
                    until status becomes <code>done</code>.
                  </p>
                </div>
              </div>

              {/* Adding Multi-Language PDFs */}
              <div className="bg-white border border-zinc-200 rounded-md p-6">
                <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-3">How to Add Multi-Language PDFs</h3>
                <ol className="space-y-3 text-sm text-zinc-700 list-decimal list-inside">
                  <li><strong>Upload the PDF</strong> via the chat interface. You will receive a CDN URL.</li>
                  <li><strong>Tell the developer</strong> which reading it belongs to (e.g. "intro", "wr001") and which language (e.g. French, Portuguese).</li>
                  <li><strong>The developer adds</strong> the URL to that reading's <span className="font-mono bg-zinc-100 px-1 rounded">pdf_urls</span> map under the correct language code.</li>
                  <li><strong>The language tab appears automatically</strong> on the reading page. No other changes needed.</li>
                </ol>
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-xs text-blue-800"><strong>Language Codes:</strong> en (English), ru (Russian), uk (Ukrainian), ko (Korean), fr (French), it (Italian), es (Spanish), pt (Portuguese), zh (Mandarin Simplified), zh-TW (Mandarin Traditional)</p>
                </div>
              </div>

              {/* Content Management */}
              <div className="bg-white border border-zinc-200 rounded-md p-6">
                <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-3">Content Management</h3>
                <div className="space-y-3 text-sm text-zinc-700">
                  <div className="border-l-4 border-blue-400 pl-4 py-2">
                    <p className="font-semibold">Readings</p>
                    <p className="text-xs text-zinc-500 mt-1">Each reading has: inline HTML content (for in-app reading + TTS), PDF documents per language (for download/printing) and a 30-minute reading timer. Use the Content tab to edit reading text. PDFs are managed via the developer.</p>
                  </div>
                  <div className="border-l-4 border-green-400 pl-4 py-2">
                    <p className="font-semibold">Lessons</p>
                    <p className="text-xs text-zinc-500 mt-1">Each lesson has: HTML content, PDF documents per language and lesson ordering. Lessons are displayed in the Content Library under the Programme tab.</p>
                  </div>
                  <div className="border-l-4 border-amber-400 pl-4 py-2">
                    <p className="font-semibold">Self-Work Tasks</p>
                    <p className="text-xs text-zinc-500 mt-1">Self-work tasks are defined in the code. Each task has sections with voice-enabled text fields, dropdowns, radio buttons and consent checkboxes. To add a new task, provide the task content to the developer.</p>
                  </div>
                </div>
              </div>

              {/* User Management */}
              <div className="bg-white border border-zinc-200 rounded-md p-6">
                <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-3">User Management</h3>
                <div className="space-y-3 text-sm text-zinc-700">
                  <div className="border-l-4 border-purple-400 pl-4 py-2">
                    <p className="font-semibold">View User Details</p>
                    <p className="text-xs text-zinc-500 mt-1">Click any user in the Users tab to see their full profile: week progression, journal count, meeting count, MH scores, personal history status and badges.</p>
                  </div>
                  <div className="border-l-4 border-red-400 pl-4 py-2">
                    <p className="font-semibold">Ban User</p>
                    <p className="text-xs text-zinc-500 mt-1">Banning a user permanently blocks their access. This cannot be undone without developer intervention.</p>
                  </div>
                  <div className="border-l-4 border-cyan-400 pl-4 py-2">
                    <p className="font-semibold">Export Criminal Justice Report</p>
                    <p className="text-xs text-zinc-500 mt-1">Generates a full unencrypted report for criminal justice sentencing. Contains personal history, stickman assessment, treatment goals and milestones. Available per user in the Users tab.</p>
                  </div>
                </div>
              </div>

              {/* Reading Timer Info */}
              <div className="bg-white border border-zinc-200 rounded-md p-6">
                <h3 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-3">Reading Timer System</h3>
                <div className="space-y-2 text-sm text-zinc-700">
                  <p>Each reading requires a minimum of <strong>30 minutes</strong> of reading time before it can be marked as complete.</p>
                  <p>Individual reading times are logged per user in the database for tracking and reporting purposes.</p>
                  <p>The timer pauses when the user navigates away. Progress is saved automatically every 30 seconds.</p>
                  <p>The TTS (Text-to-Speech) "Listen to Reading" feature reads the first section of each document aloud in 8 supported languages.</p>
                </div>
              </div>

              {/* Security Overview */}
              <div className="bg-zinc-900 text-white rounded-md p-6">
                <h3 className="text-sm font-bold uppercase tracking-wider mb-3">Security Checklist</h3>
                <div className="space-y-2 text-xs text-zinc-300">
                  <div className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-green-400" /> Enable 2FA on your SuperAdmin account (2FA Setup tab)</div>
                  <div className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-green-400" /> Review blocked IPs regularly (Security tab)</div>
                  <div className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-green-400" /> Monitor suspicious login attempts</div>
                  <div className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-green-400" /> Invite codes: AXIOM2026, ARP2026, RECOVERY, SILKWORTH, TUDOR8</div>
                  <div className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-green-400" /> All user data is stored in MongoDB with encryption at rest</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <BackToTopButton />
    </div>
  );
}

export default SuperAdminPage;
